import { Component, Input, OnInit } from '@angular/core';
import { AppSettings } from '../../app.settings';
import { ArtworkCardSize } from '../ds-artwork-card/ds-artwork-card.component';

/**
 * Responsive artwork grid + a per-grid S/M/L size control.
 *
 * Owns three things so every artwork list behaves identically: the responsive grid
 * layout, the size (density) control, and per-context persistence (localStorage, keyed
 * by `context`). Cards are projected in; expose the chosen size to them with a template
 * ref, e.g.:
 *
 *   <ds-artwork-grid context="gallery-inventory" #g>
 *     @for (p of items; track p.Id) {
 *       <ds-artwork-card [size]="g.size" [imageUrl]="..." ...></ds-artwork-card>
 *     }
 *   </ds-artwork-grid>
 */
@Component({
    selector: 'ds-artwork-grid',
    templateUrl: './ds-artwork-grid.component.html',
    styleUrls: ['./ds-artwork-grid.component.css'],
    standalone: false
})
export class DsArtworkGridComponent implements OnInit {
    /** Distinguishes this grid's saved size from others (e.g. 'gallery-inventory'). */
    @Input() context = 'default';
    @Input() showSizeControl = true;

    public size: ArtworkCardSize = 'md';

    private readonly minBySize: Record<ArtworkCardSize, number> = { sm: 140, md: 200, lg: 270 };

    public ngOnInit(): void {
        const saved = this.read();
        if (saved) { this.size = saved; }
    }

    public get minPx(): number { return this.minBySize[this.size]; }

    public setSize(s: ArtworkCardSize): void {
        this.size = s;
        try { localStorage.setItem(this.key(), s); } catch { /* storage unavailable — ignore */ }
    }

    private read(): ArtworkCardSize | null {
        try {
            const v = localStorage.getItem(this.key());
            return (v === 'sm' || v === 'md' || v === 'lg') ? v : null;
        } catch { return null; }
    }

    private key(): string { return AppSettings.ARTWORK_CARD_SIZE_PREFIX + this.context; }
}
